// client/src/pages/auth/VerifyEmail.js — Maxula
// P0-B (v1.151) : page de vérification d'adresse e-mail.
// 3 états : chargement / succès / erreur (avec renvoi du lien).
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiLoader, FiMail } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const { user } = useAuth() || {};
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // évite le double appel StrictMode
    ran.current = true;
    if (!token) { setState('error'); setMessage('Lien invalide ou expiré.'); return; }
    (async () => {
      try {
        const r = await authAPI.verifyEmail(token);
        setState('success');
        setMessage(r?.data?.message || 'Email vérifié avec succès.');
      } catch (e) {
        setState('error');
        setMessage(e?.response?.data?.message || 'Lien invalide ou expiré.');
      }
    })();
  }, [token]);

  const resend = async () => {
    setResending(true);
    try {
      const r = await authAPI.resendVerification();
      toast.success(r?.data?.message || 'Email renvoyé. Vérifiez votre boîte mail.');
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-2xl shadow p-8 w-full max-w-md text-center">
        {state === 'loading' && (
          <>
            <span className="mx-auto w-14 h-14 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center mb-4">
              <FiLoader size={26} className="animate-spin" />
            </span>
            <h1 className="text-xl font-bold mb-1">Vérification en cours...</h1>
            <p className="text-sm text-gray-600">Un instant, nous confirmons votre adresse e-mail.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <span className="mx-auto w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mb-4">
              <FiCheckCircle size={28} />
            </span>
            <h1 className="text-xl font-bold mb-1">Email vérifié !</h1>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            <Link to={user ? '/dashboard' : '/login'}
              className="inline-block px-6 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700">
              {user ? 'Aller à mon tableau de bord' : 'Se connecter'}
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <span className="mx-auto w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
              <FiXCircle size={28} />
            </span>
            <h1 className="text-xl font-bold mb-1">Lien invalide ou expiré</h1>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            {user ? (
              <button type="button" disabled={resending} onClick={resend}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-50">
                <FiMail size={16} /> {resending ? 'Envoi...' : "Renvoyer l'email"}
              </button>
            ) : (
              <p className="text-sm text-gray-600">
                <Link to="/login" className="text-primary-600 font-semibold hover:underline">Connectez-vous</Link>
                {' '}pour renvoyer un nouveau lien de vérification.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
